import { Global } from "../../utils/Global";

export class BasicWindow extends Phaser.GameObjects.Container {
  protected background: Phaser.GameObjects.NineSlice;
  protected contentContainer: Phaser.GameObjects.Container;

  private onCloseCallback?: () => void;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    textureKey: string,
    frameKey: string,
    sliceWidth: number,
    sliceHeight: number,
    leftWidth: number,
    topHeight: number,
    rightWidth: number,
    bottomHeight: number,
  ) {
    super(scene);

    this.setSize(width, height);
    this.setPosition(x, y);

    this.background = new Phaser.GameObjects.NineSlice(
      scene,
      0,
      0,
      textureKey,
      frameKey,
      sliceWidth,
      sliceHeight,
      leftWidth,
      rightWidth,
      topHeight,
      bottomHeight,
    ).setTint(0x9c7a4d);
    this.add(this.background);

    this.contentContainer = new Phaser.GameObjects.Container(scene, 0, 0);
    this.add(this.contentContainer);

    // initially hidden
    this.setVisible(false);
  }

  public resize(width: number, height: number): void {
    this.background.setSize(width, height);
    this.contentContainer.setSize(width, height);
  }

  public addContent(content: Phaser.GameObjects.GameObject): void {
    this.contentContainer.add(content);
  }

  public removeContent(content: Phaser.GameObjects.GameObject): void {
    this.contentContainer.remove(content);
  }

  public clearContent(): void {
    this.contentContainer.removeAll();
  }

  public show(onComplete?: () => void): void {
    if (this.visible) return;

    this.setScale(0.8);
    this.setAlpha(0);
    this.setVisible(true);

    this.scene.tweens.add({
      targets: this,
      scale: 1,
      alpha: 1,
      duration: 100,
      ease: "Back.easeOut",
      onComplete: () => {
        if (onComplete) onComplete();
      },
    });
  }

  public hide(onComplete?: () => void): void {
    if (!this.visible) return;

    this.scene.tweens.add({
      targets: this,
      scale: 0.8,
      alpha: 0,
      duration: 100,
      ease: "Back.easeIn",
      onComplete: () => {
        this.setVisible(false);
        if (onComplete) onComplete();
      },
    });
  }

  public close(): void {
    this.hide(() => {
      if (this.onCloseCallback) {
        this.onCloseCallback();
      }
    });
  }

  public setOnCloseCallback(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  public getContentContainer(): Phaser.GameObjects.Container {
    return this.contentContainer;
  }

  protected resizeToFitContent(): void {
    const padding = Global.minFontSize; // Dynamic padding based on font size

    // Get all children from the content container
    const children = this.contentContainer.list;

    if (children.length === 0) {
      // No content, use minimal size
      this.resize(padding * 2, padding * 2);
      return;
    }

    let leftmost = Number.MAX_VALUE;
    let rightmost = Number.MIN_VALUE;
    let topmost = Number.MAX_VALUE;
    let bottommost = Number.MIN_VALUE;

    // Iterate through all children and find bounds
    children.forEach((child) => {
      const gameObject = child as Phaser.GameObjects.GameObject & {
        getBounds?: () => Phaser.Geom.Rectangle;
        displayWidth?: number;
        displayHeight?: number;
        x?: number;
        y?: number;
        originX?: number;
        originY?: number;
      };

      if (gameObject && typeof gameObject.getBounds === "function") {
        // Force bounds update by accessing displayWidth/displayHeight first
        const displayWidth = gameObject.displayWidth || 0;
        const displayHeight = gameObject.displayHeight || 0;

        const x = gameObject.x || 0;
        const y = gameObject.y || 0;
        const originX =
          gameObject.originX !== undefined ? gameObject.originX : 0.5;
        const originY =
          gameObject.originY !== undefined ? gameObject.originY : 0.5;

        // Use displayWidth/displayHeight instead of getBounds() for more reliable sizing
        const left = x - displayWidth * originX;
        const right = x + displayWidth * (1 - originX);
        const top = y - displayHeight * originY;
        const bottom = y + displayHeight * (1 - originY);

        leftmost = Math.min(leftmost, left);
        rightmost = Math.max(rightmost, right);
        topmost = Math.min(topmost, top);
        bottommost = Math.max(bottommost, bottom);
      }
    });

    // Calculate content dimensions
    const contentWidth = rightmost - leftmost;
    const contentHeight = bottommost - topmost;

    // Calculate content center
    const contentCenterX = (leftmost + rightmost) / 2;
    const contentCenterY = (topmost + bottommost) / 2;

    // Calculate window size with padding
    const windowWidth = Math.ceil(contentWidth + padding * 2);
    const windowHeight = Math.ceil(contentHeight + padding * 2);

    // Resize the window first
    this.resize(windowWidth, windowHeight);

    // Now shift all content so it's centered in the window
    // The background is centered at (0,0), so content should also be centered around (0,0)
    children.forEach((child) => {
      const gameObject = child as Phaser.GameObjects.GameObject & {
        x?: number;
        y?: number;
      };
      if (
        gameObject &&
        gameObject.x !== undefined &&
        gameObject.y !== undefined
      ) {
        gameObject.x -= contentCenterX;
        gameObject.y -= contentCenterY;
      }
    });
  }
}
